import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  lisConfigApi,
  type LisNamedPath,
  type LisPreMap,
  type LisPreMapKeyInput,
  type LisPreMapPreview,
  type LisSourceSchema,
} from '@/api/lisConfigApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * Pre-map and mapping configuration. Every key carries `selectedTenantId`, so
 * switching tenant cannot leave another tenant's configuration on screen — these
 * decide where a clinical result lands, so showing the wrong tenant's would be
 * worse than showing none.
 */
export function useLisSourceSchemas() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-config', 'schemas', tenantId],
    enabled: !!tenantId,
    queryFn: () => lisConfigApi.listSchemas(),
  })
}

export function useLisPreMaps() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-config', 'pre-maps', tenantId],
    enabled: !!tenantId,
    queryFn: () => lisConfigApi.listPreMaps(),
  })
}

/**
 * The actions a mapping can take on a repeat delivery.
 *
 * Not keyed by tenant: the catalogue is the same everywhere, and it changes only
 * when the code does. `staleTime: Infinity` keeps it off the wire after the
 * first read.
 */
export function useLisRecordActions() {
  return useQuery({
    queryKey: ['lis-config', 'record-actions'],
    queryFn: () => lisConfigApi.listRecordActions(),
    staleTime: Infinity,
  })
}

export function useLisMappingFailures(includeResolved = false) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: ['lis-config', 'failures', tenantId, includeResolved],
    enabled: !!tenantId,
    queryFn: () => lisConfigApi.listFailures(includeResolved),
  })
}

/** Every mutation invalidates the same subtree; the screen is small. */
function useConfigMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient()
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lis-config'] })
    },
  })
}

export function useCreateLisSourceSchema() {
  return useConfigMutation<
    {
      name: string
      description?: string
      sample: unknown
      orderIdPath: string
      testCodePath: string
    },
    LisSourceSchema
  >((b) => lisConfigApi.createSchema(b))
}

export function useUpdateLisSourceSchema() {
  return useConfigMutation<
    {
      id: string
      name?: string
      description?: string | null
      sample?: unknown
      orderIdPath?: string
      testCodePath?: string
      active?: boolean
    },
    LisSourceSchema
  >(({ id, ...body }) => lisConfigApi.updateSchema(id, body))
}

export function useCreateLisNamedPath() {
  return useConfigMutation<{ schemaId: string; name: string; path: string }, LisNamedPath>(
    ({ schemaId, ...body }) => lisConfigApi.createNamedPath(schemaId, body),
  )
}

export function useUpdateLisNamedPath() {
  return useConfigMutation<{ id: string; name?: string; path?: string }, LisNamedPath>(
    ({ id, ...body }) => lisConfigApi.updateNamedPath(id, body),
  )
}

export function useRemoveLisNamedPath() {
  return useConfigMutation<string, { removed: boolean }>((id) => lisConfigApi.removeNamedPath(id))
}

export function useRemoveLisSourceSchema() {
  return useConfigMutation<string, { removed: boolean }>((id) => lisConfigApi.removeSchema(id))
}

export function useCreateLisPreMap() {
  return useConfigMutation<
    {
      schemaId: string
      description?: string
      valuePath: string
      keys?: LisPreMapKeyInput[]
      destination?: {
        formDefinitionId: string
        targetFieldId: string
        action?: string
      }
    },
    LisPreMap
  >((b) => lisConfigApi.createPreMap(b))
}

export function useUpdateLisPreMap() {
  return useConfigMutation<
    {
      id: string
      description?: string | null
      valuePath?: string
      active?: boolean
      keys?: LisPreMapKeyInput[]
    },
    LisPreMap
  >(({ id, ...body }) => lisConfigApi.updatePreMap(id, body))
}

export function useRemoveLisPreMap() {
  return useConfigMutation<string, { removed: boolean }>((id) => lisConfigApi.removePreMap(id))
}

/**
 * Trying a draft against the sample.
 *
 * A mutation rather than a query even though it writes nothing: it is a POST
 * (the document does not fit in a URL) and it must run exactly when asked, not
 * be cached and replayed on a key change.
 */
export function usePreviewLisPreMap() {
  return useMutation<
    LisPreMapPreview,
    Error,
    { schemaId: string; valuePath: string; keys?: LisPreMapKeyInput[]; document?: unknown }
  >({ mutationFn: (body) => lisConfigApi.previewPreMap(body) })
}

export function useCreateLisMapping() {
  return useConfigMutation<
    {
      preMapId: string
      formDefinitionId: string
      targetFieldId: string
    },
    unknown
  >(({ preMapId, ...body }) => lisConfigApi.createMapping(preMapId, body))
}

export function useUpdateLisMapping() {
  return useConfigMutation<
    {
      id: string
      formDefinitionId?: string
      targetFieldId?: string
      action?: string
    },
    unknown
  >(({ id, ...body }) => lisConfigApi.updateMapping(id, body))
}

export function useRemoveLisMapping() {
  return useConfigMutation<string, { removed: boolean }>((id) => lisConfigApi.removeMapping(id))
}

export function useResolveLisFailure() {
  return useConfigMutation<string, unknown>((id) => lisConfigApi.resolveFailure(id))
}
